<template>
  <Message v-if="validationError" :closeable="false" severity="error">{{ validationError }}</Message>
  <div v-if="props.report != undefined && props.report.defaultTemplate != null">
    <form @submit="onSubmitSaveAs">
      <Toolbar pt:root:class="mx-4 mt-3 py-3">
        <template #start>
          <div class="flex flex-row gap-2">
            <div class="flex flex-col">
              <label for="tagFilter" class="font-bold">Filter Tags</label>
              <InputGroup class="h-10">
                <StackedLabelMultiSelectNoForm
                  name="tagFilter"
                  v-model="filterTags"
                  :options="allTags"
                  option-label="name"
                  label=""
                  input-class="w-full rounded-r-none"
                  size="w-96"
                  placeholder="Filter Tags"
                  :disabled="showAll"
                  @change="filtersChanged"
                ></StackedLabelMultiSelectNoForm>
                <InputGroupAddon class="w-24">
                  <small class="mr-1">Show All</small>
                  <Checkbox v-model="showAll" :binary="true" size="small" @value-change="filtersChanged"></Checkbox>
                </InputGroupAddon>
              </InputGroup>
            </div>
            <StackedLabelDropdownNoForm
              name="template"
              label="Template"
              :options="filteredTemplates"
              optionsLabel="name"
              v-model="selectedTemplate"
              @change="selectTemplate"
              size="w-72"
            ></StackedLabelDropdownNoForm>
          </div>
        </template>
        <template #end>
          <div class="flex flex-row gap-2 me-2 items-end">
            <div class="h-10">
              <IconButton
                v-if="locking"
                :icon="isCurrentTemplateLocked ? 'lock pi-spinner' : 'lock-open pi-spinner'"
                :severity="isCurrentTemplateLocked ? '' : 'warning'"
                :disabled="true"
              ></IconButton>
              <IconButton
                v-else-if="savingAs || deleting || saving"
                :icon="isCurrentTemplateLocked ? 'lock' : 'lock-open'"
                :severity="isCurrentTemplateLocked ? '' : 'warning'"
                :disabled="true"
              ></IconButton>
              <IconButton
                v-else
                :icon="isCurrentTemplateLocked ? 'lock' : 'lock-open'"
                :disabled="selectedTemplate == null || selectedTemplate.defaultLayout"
                @click="isCurrentTemplateLocked ? unlockCurrent() : lockCurrent()"
              ></IconButton>
            </div>
            <IconButton
              v-tooltip.top="'Save'"
              :icon="saving ? 'spin pi-spinner' : 'save'"
              :disabled="
                saving || savingAs || deleting || locking || selectedTemplate == null || selectedTemplate.defaultLayout || isCurrentTemplateLocked
              "
              severity="success"
              @click="saveCurrent"
            ></IconButton>

            <StackedLabelInputNoForm
              name="newTemplateName"
              label="Template Name"
              v-model="newTemplateName"
              size="w-44"
              :disabled="selectedTemplate == null"
            ></StackedLabelInputNoForm>
            <StackedLabelMultiSelectNoForm
              name="tags"
              v-model="templateTags"
              :options="allTags"
              option-label="name"
              label="Tags"
              size="w-96"
              :disabled="selectedTemplate == null"
            ></StackedLabelMultiSelectNoForm>
            <Button
              label="Save As"
              :icon="savingAs ? 'pi pi-spin pi-spinner' : ''"
              iconPos="right"
              class="h-10 mt-auto"
              :disabled="savingAs || saving || deleting || locking || selectedTemplate == null"
              @click="saveAsNew"
            ></Button>

            <Button
              :icon="deleting ? 'pi pi-spin pi-spinner' : ''"
              iconPos="right"
              severity="danger"
              label="Delete"
              class="h-10 mt-auto"
              :disabled="
                deleting || saving || savingAs || locking || selectedTemplate == null || selectedTemplate.defaultLayout || isCurrentTemplateLocked
              "
              @click="deleteCurrent"
            ></Button>
          </div>
        </template>
      </Toolbar>
    </form>
    <ag-grid-vue
      v-show="selectedTemplate"
      theme="legacy"
      :immutableData="true"
      :getRowId="(params: GetRowIdParams) => selectedTemplate?.name + params.data.id"
      :rowData="selectedTemplate?.columns"
      :columnDefs="colDefs"
      :defaultColDef="defaultColDefs"
      :autoSizeStrategy="{ type: 'fitGridWidth' }"
      domLayout="autoHeight"
      :suppressCellFocus="true"
      class="mx-4 py-3"
      :class="darkVal ? 'ag-theme-quartz-dark' : 'ag-theme-quartz'"
      :singleClickEdit="true"
      :stopEditingWhenCellsLoseFocus="true"
      :suppressDragLeaveHidesColumns="true"
      :rowDragManaged="true"
      :rowDragEntireRow="true"
      :grid-options="{
        rowDragMultiRow: true,
        rowSelection: 'multiple',
      }"
      @row-drag-leave="onRowDragEnd"
      @row-drag-end="onRowDragEnd"
      @grid-ready="(event: GridReadyEvent) => (gridApi = event.api)"
    >
    </ag-grid-vue>
    <div v-show="selectedTemplate == null" class="flex h-[50vh]">
      <p class="w-fit flex font-bold text-surface-500 m-auto">No Template Selected</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { AgGridVue } from "ag-grid-vue3";
import { usePropsStore } from "@/stores/props";
import { useForm } from "vee-validate";
import CustomCheckboxRenderer from "@/components/cell-renderers/CustomCheckboxRenderer.vue";
import { getUserPermissions } from "@/services/Permissions";
import { toFormData, getCellEditableClass, confirmPopup, getCodeTable } from "@/services/Utility";
import { UserPermissionsSchema, type UserPermissions } from "@/types/User";
import { useAuthStore } from "@/stores/auth";
import { TemplateStatus } from "@/enums/TemplateStatus";
import { type ReportData, type ReportTemplateList, type ReportTemplate } from "@/types/Reports";
import { useConfirm } from "primevue/useconfirm";
import TemplateColumnPosRenderer from "../cell-renderers/TemplateColumnPosRenderer.vue";
import type { GetRowIdParams, GridApi, GridReadyEvent, GridSizeChangedEvent, RowDataUpdatedEvent, ValueGetterParams } from "ag-grid-community";
import type { Code } from "@/types/Codes";
import { CodeTableNumber } from "@/enums/CodeTable";

const gridApi = ref<GridApi>();
const templates = ref<ReportTemplate[]>([]);
const filteredTemplates = ref<ReportTemplate[]>([]);
const selectedTemplate = ref<ReportTemplate>();
const allTags = ref<Code[]>([]);
const filterTags = ref<Code[]>([]);
const templateTags = ref<Code[]>([]);

const validationError = ref("");
const newTemplateName = ref("");
const saving = ref(false);
const savingAs = ref(false);
const deleting = ref(false);
const locking = ref(false);
const showAll = ref(false);

interface Props {
  report: ReportData;
}
const props = defineProps<Props>();

const propsStore = usePropsStore();
const userStore = useAuthStore();
const userPermissions = ref<UserPermissions>(UserPermissionsSchema.parse({}));
const darkVal = computed<boolean>(() => propsStore.darkMode);
const confirm = useConfirm();

const emit = defineEmits(["submitted"]);

const { handleSubmit } = useForm({
  keepValuesOnUnmount: true,
});

watch(
  () => props.report,
  (report: ReportData | undefined) => {
    if (report != null) populateTables(report.defaultTemplate);
  }
);

const isCurrentTemplateLocked = computed<boolean>(() => {
  if (selectedTemplate.value == null) return false;
  return selectedTemplate.value.lockedBy != null && selectedTemplate.value.lockedBy.trim().length > 0;
});

const filtersChanged = (defaultTemplate: string | null = null): void => {
  let selectedChanged: boolean = true;
  if (showAll.value) {
    filteredTemplates.value = templates.value;
    if (selectedTemplate.value != null) selectedChanged = false;
  } else {
    filteredTemplates.value = [];
    let filterList: number[] = filterTags.value.map((tag: Code) => tag.num);
    let noTags: boolean = filterList.length == 0;
    templates.value.forEach((template: ReportTemplate) => {
      if (template.name === "<Default Template>") {
        if (noTags) {
          filteredTemplates.value.push(template);
          if (selectedTemplate.value == template) selectedChanged = false;
        }
      } else {
        let tags: number[];
        if (template.tags == null || template.tags === "") tags = [];
        else tags = template.tags.split(",").map((tag: string) => parseInt(tag));
        if (noTags) {
          if (tags.length == 0) {
            filteredTemplates.value.push(template);
            if (selectedTemplate.value == template) selectedChanged = false;
          }
        } else {
          let tagsExist: boolean = true;
          filterList.forEach((filter: number) => {
            if (!tags.includes(filter)) tagsExist = false;
          });
          if (tagsExist) {
            filteredTemplates.value.push(template);
            if (selectedTemplate.value == template) selectedChanged = false;
          }
        }
      }
    });
  }

  if (selectedChanged) selectDefault(defaultTemplate);
};

const selectDefault = (defaultTemplate: string | null = null): void => {
  selectedTemplate.value = undefined;
  filteredTemplates.value.forEach((temp) => {
    if (temp.name === defaultTemplate) {
      selectedTemplate.value = temp;
    }
  });
  if (selectedTemplate.value == null) selectedTemplate.value = filteredTemplates.value[0];
  selectTemplate();
};

const onSubmitSaveAs = handleSubmit((values: any): void => {
  saveAsNew();
});

const populateTables = (defaultTemplate: string | null = null): void => {
  templates.value = [];
  selectedTemplate.value = undefined;

  if (props.report == null) return;

  axios
    .get("/reports/templates/" + props.report.id)
    .then((response) => {
      handleApiResponse(
        response,
        (templateList: ReportTemplateList) => {
          selectedTemplate.value = undefined;
          templates.value = templateList.templates;
          filtersChanged(defaultTemplate);
        },
        (errors: ApiError[]) => {
          validationError.value = errors[0].message;
        }
      );
    })
    .catch((e) => errorApi(e));
};

const changeEnabled = (rowIndex: number, isEnabled: boolean): void => {
  const rowData = gridApi.value?.getDisplayedRowAtIndex(rowIndex);
  if (rowData) rowData.data.enabled = isEnabled;
};

const onRowDragEnd = (e: Event): void => {
  if (selectedTemplate.value && gridApi.value) selectedTemplate.value.columns = gridApi.value.getRenderedNodes().map((node) => node.data);
  gridApi.value?.refreshCells();
};

const selectTemplate = (): void => {
  if (selectedTemplate.value == null) return;
  if (selectedTemplate.value.defaultLayout) newTemplateName.value = "";
  else newTemplateName.value = selectedTemplate.value.name;

  const tags: number[] = selectedTemplate.value.tags.split(",").map((tag: string) => parseInt(tag));
  templateTags.value = [];
  tags.forEach((tag: number) => {
    const tagCode: Code | undefined = allTags.value.find((t) => t.num == tag);
    if (tagCode != null) templateTags.value.push(tagCode);
  });
};

const saveCurrent = (): void => {
  if (selectedTemplate.value == null) return;
  saving.value = true;
  let name = newTemplateName.value;
  if (name == null || name.trim().length == 0) name = selectedTemplate.value.name;
  if (name.localeCompare(selectedTemplate.value.name) == 0) {
    performSave(selectedTemplate.value, name);
  } else {
    confirmPopup(
      confirm,
      "Saving Template",
      "Rename template from '" + selectedTemplate.value.name + "' to '" + name + "'?",
      () => checkNameAndPerformSave(selectedTemplate.value!, name),
      () => (saving.value = false)
    );
  }
};

const saveAsNew = (): void => {
  if (newTemplateName.value == null || newTemplateName.value.trim().length == 0) {
    error("Cannot save template", "The template name cannot be blank when saving as a new template.");
    return;
  }
  savingAs.value = true;
  let newTemplate = <ReportTemplate>{};
  newTemplate.name = newTemplateName.value;
  if (newTemplate.name == null || newTemplate.name.trim().length == 0) return;
  newTemplate.defaultLayout = false;
  newTemplate.columns = selectedTemplate.value!.columns;

  if (newTemplate.name.localeCompare(selectedTemplate.value!.name) == 0) {
    performSave(newTemplate, newTemplate.name);
  } else {
    checkNameAndPerformSave(newTemplate, newTemplate.name);
  }
};

const checkNameAndPerformSave = (newTemplate: ReportTemplate, name: string): void => {
  axios
    .get("/reports/templates/" + props.report.id + "/check", {
      params: {
        name: encodeURIComponent(name),
      },
    })
    .then((response) => {
      handleApiResponse(
        response,
        (result: TemplateStatus) => {
          if (result == TemplateStatus.LOCKED) {
            error("Template is locked", "A locked template with the name '" + newTemplate.name + "'already exists. Please choose a different name.");
            saving.value = false;
            savingAs.value = false;
          } else if (result == TemplateStatus.EDITABLE) {
            confirmPopup(
              confirm,
              "Saving Template",
              "Overwrite existing template named '" + name + "'?",
              () => performSave(newTemplate, name),
              () => {
                saving.value = false;
                savingAs.value = false;
              }
            );
          } else {
            performSave(newTemplate, name);
          }
        },
        (errors: ApiError[]) => {
          error("Error when checking template names", errors[0].message);
          saving.value = false;
          savingAs.value = false;
        }
      );
    })
    .catch((e) => errorApi(e));
};

const performSave = (newTemplate: ReportTemplate, name: string): void => {
  newTemplate.tags = templateTags.value.map((tag) => tag.num).join(",");
  let formData = toFormData(newTemplate);
  formData.append("newName", name);

  axios
    .post("/reports/templates/" + props.report.id + "/save", formData)
    .then((response) => {
      handleApiResponse(
        response,
        (result: string) => {
          success("Template saved.", "");
          const newTags: number[] = newTemplate.tags.split(",").map((tag: string) => parseInt(tag));
          filterTags.value = filterTags.value.filter((tag: Code) => newTags.includes(tag.num));
          populateTables(name);
        },
        (errors: ApiError[]) => {
          error("Cannot save template", errors[0].message);
        }
      );
    })
    .catch((e) => errorApi(e))
    .finally(() => {
      saving.value = false;
      savingAs.value = false;
    });
};

const deleteCurrent = (): void => {
  if (selectedTemplate.value == null) return;
  deleting.value = true;
  confirmPopup(
    confirm,
    "Deleting Template",
    "Delete the template named '" + selectedTemplate.value.name + "'?",
    deleteTemplate,
    () => (deleting.value = false)
  );
};

const deleteTemplate = (): void => {
  let formData = toFormData(selectedTemplate.value!);
  axios
    .post("/reports/templates/" + props.report.id + "/delete", formData)
    .then((response) => {
      handleApiResponse(
        response,
        (result: string) => {
          success("Template deleted.", "");
          populateTables(null);
        },
        (errors: ApiError[]) => {
          error("Cannot delete template", errors[0].message);
        }
      );
    })
    .catch((e) => errorApi(e))
    .finally(() => {
      deleting.value = false;
    });
};

const lockCurrent = (): void => {
  if (selectedTemplate.value == null) return;
  locking.value = true;
  confirmPopup(
    confirm,
    "Locking Template",
    "Lock the template named '" + selectedTemplate.value.name + "' for editing? No changes can be made to it until it is unlocked again.",
    lockTemplate,
    () => (locking.value = false)
  );
};

const lockTemplate = (): void => {
  axios
    .post("/reports/templates/" + props.report.id + "/lock?templateName=" + selectedTemplate.value?.name)
    .then((response) => {
      handleApiResponse(
        response,
        (result: string) => {
          success("Template locked.", "");
          selectedTemplate.value!.lockedBy = result;
        },
        (errors: ApiError[]) => {
          error("Cannot lock template", errors[0].message);
        }
      );
    })
    .catch((e) => errorApi(e))
    .finally(() => {
      locking.value = false;
    });
};

const unlockCurrent = (): void => {
  if (selectedTemplate.value == null) return;
  locking.value = true;
  confirmPopup(
    confirm,
    "Unlocking Template",
    "Unlock the template named '" + selectedTemplate.value.name + "' for editing? It will once again be able to be modified and deleted.",
    unlockTemplate,
    () => (locking.value = false)
  );
};

const unlockTemplate = (): void => {
  axios
    .post("/reports/templates/" + props.report.id + "/unlock?templateName=" + selectedTemplate.value?.name)
    .then((response) => {
      handleApiResponse(
        response,
        (result: boolean) => {
          locking.value = false;
          if (result) {
            success("Template unlocked.", "");
            selectedTemplate.value!.lockedBy = null;
          } else {
            locking.value = false;
            error("Template was not unlocked", "Result was false");
          }
        },
        (errors: ApiError[]) => {
          locking.value = false;
          error("Cannot unlock template", errors[0].message);
        }
      );
    })
    .catch((e) => errorApi(e));
};

const colDefs = ref([
  {
    width: 0,
    maxWidth: 35,
    editable: false,
    rowDrag: true,
  },
  {
    headerName: "Position",
    cellDataType: "number",
    headerClass: "grid-align-center text-center",
    cellClass: "text-center",
    width: 75,
    maxWidth: 75,
    wrapText: true,
    autoHeight: true,
    valueGetter: (params: ValueGetterParams) => params.node?.rowIndex,
    editable: false,
  },
  {
    headerName: "Type",
    headerClass: "grid-align-center text-center",
    cellClass: "text-center",
    width: 125,
    maxWidth: 125,
    wrapText: true,
    autoHeight: true,
    editable: false,
    cellRenderer: TemplateColumnPosRenderer,
  },
  {
    field: "header",
    headerName: "Header",
    width: 200,
    wrapText: true,
    autoHeight: true,
  },
  {
    field: "width",
    headerName: "Width",
    width: 25,
  },
  {
    field: "enabled",
    headerName: "Enabled",
    editable: false,
    width: 0,
    cellDataType: "boolean",
    cellRenderer: CustomCheckboxRenderer,
    cellRendererParams: {
      setValue: changeEnabled,
    },
    cellClass: () => getCellEditableClass() + " text-center",
  },
]);

const resize = (event: GridSizeChangedEvent | RowDataUpdatedEvent): void => {
  event.api.sizeColumnsToFit();
};

const defaultColDefs = {
  filter: false,
  sortable: false,
  floatingFilter: false,
  cellDataType: "text",
  editable: true,
  resizable: false,
};

onMounted(async (): Promise<void> => {
  await getUserPermissions(userStore.getUserInfo().userId).then((result: UserPermissions) => (userPermissions.value = result));
  await getCodeTable(CodeTableNumber.REPORT_TAGS).then((result: Code[]) => (allTags.value = result));
  populateTables(props.report.defaultTemplate);
});
</script>
